import moment from "moment";
import queryString from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFormsySubmissionsForClient,
  getSubmission,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScripts";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldValue,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  Checkbox,
  Input,
  PhoneInput,
  RadioButton,
  Select,
  TextArea,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import { getSchema } from "./joi/validation";

function KYF216() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [medicationData, setMedicationData] = useState({
    Name: "",
    Dosage: "",
    Frequency: "",
    Quantity: "",
    Refills: "",
    LastRefillDate: "",
  });

  const handleMedicationChange = ({ currentTarget: input }) => {
    const mData = { ...medicationData };
    mData[input.name] = input.value;
    setMedicationData(mData);
  };

  const handleAddMedication = () => {
    const obj = { ...data };
    let details = obj.MedicationDetails["@value"] || "";
    if (details !== "") details += "\n";
    details += `Name of Medication: ${medicationData.Name}. Dosage: ${
      medicationData.Dosage
    }. Frequency: ${medicationData.Frequency}. Quantity Received: ${
      medicationData.Quantity
    }. # of Refills: ${medicationData.Refills}. Date of Last Refill: ${moment(
      medicationData.LastRefillDate
    ).format("L")}`;
    obj.MedicationDetails["@value"] = details;
    setData(obj);
    setMedicationData({
      Name: "",
      Dosage: "",
      Frequency: "",
      Quantity: "",
      Refills: "",
      LastRefillDate: "",
    });
  };

  const gradeOptions = [
    { _id: "0", name: "Pre-School/Pre-K" },
    { _id: "1", name: "1" },
    { _id: "2", name: "2" },
    { _id: "3", name: "3" },
    { _id: "4", name: "4" },
    { _id: "5", name: "5" },
    { _id: "6", name: "6" },
    { _id: "7", name: "7" },
    { _id: "8", name: "8" },
    { _id: "9", name: "9" },
    { _id: "10", name: "10" },
    { _id: "11", name: "11" },
    { _id: "12", name: "12" },
  ];

  const performingOptions = [
    { _id: "1", name: "At Grade Level " },
    { _id: "2", name: "Below Grade Level " },
    { _id: "3", name: "Above Grade Level " },
  ];

  const placementOptions = [
    { _id: "1", name: "1" },
    { _id: "2", name: "2" },
    { _id: "3", name: "3" },
    { _id: "4", name: "4" },
    { _id: "5", name: "5" },
    { _id: "6", name: "6" },
    { _id: "7", name: "7" },
    { _id: "8", name: "8" },
    { _id: "9", name: "9" },
    { _id: "10", name: "10" },
    { _id: "11", name: "11" },
    { _id: "12", name: "12" },
    { _id: "13", name: "13" },
    { _id: "14", name: "14" },
    { _id: "15", name: "15" },
    { _id: "16", name: "16" },
    { _id: "17", name: "17" },
    { _id: "18", name: "18" },
    { _id: "19", name: "19" },
    { _id: "20", name: "20" },
    { _id: "21", name: "21" },
    { _id: "22", name: "22" },
    { _id: "23", name: "23" },
    { _id: "24", name: "24" },
    { _id: "25", name: "25" },
    { _id: "26", name: "26" },
    { _id: "27", name: "27" },
    { _id: "28", name: "28" },
    { _id: "29", name: "29" },
    { _id: "30", name: "30" },
  ];

  const yesNoOptions = [
    { _id: "1", name: "Yes" },
    { _id: "0", name: "No" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <Input
                name="DatePassportGiven"
                label="Date Medical Passport (Forms DSS106, 106A and 106A-1 through 106A-6) was given to Foster Parents:"
                error={errors.DatePassportGiven}
                value={data.DatePassportGiven["@value"]}
                onChange={handleChange}
                identical={compare("DatePassportGiven")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <Select
                name="ChildCurrentGrade"
                label="Child's Current Grade:"
                options={gradeOptions}
                error={errors.ChildCurrentGrade}
                value={data.ChildCurrentGrade["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <RadioButton
                name="ChildCurrentlyPerforming"
                label="Child is performing:"
                options={performingOptions}
                error={errors.ChildCurrentlyPerforming}
                value={data.ChildCurrentlyPerforming["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="PreviousSchoolInfo"
                label="Name and address of school child previously attended:"
                error={errors.PreviousSchoolInfo}
                value={data.PreviousSchoolInfo["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <TextArea
                name="NewSchoolInfo"
                label="Name and address of school child will be attending if different:"
                error={errors.NewSchoolInfo}
                value={data.NewSchoolInfo["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <div className="row">
                <div className="col">
                  <Input
                    name="Height"
                    label="Height:"
                    error={errors.Height}
                    value={data.Height["@value"]}
                    onChange={handleChange}
                    identical={compare("Height")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="Weight"
                    label="Weight:"
                    error={errors.Weight}
                    value={data.Weight["@value"]}
                    onChange={handleChange}
                    identical={compare("Weight")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="EyeColor"
                    label="Eye color:"
                    error={errors.EyeColor}
                    value={data.EyeColor["@value"]}
                    onChange={handleChange}
                    identical={compare("EyeColor")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <Input
                    name="HairColor"
                    label="Hair color:"
                    error={errors.HairColor}
                    value={data.HairColor["@value"]}
                    onChange={handleChange}
                    identical={compare("HairColor")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              <Input
                name="IdentifyingMarks"
                label="Identifying marks, scars, piercings:"
                error={errors.IdentifyingMarks}
                value={data.IdentifyingMarks["@value"]}
                onChange={handleChange}
                identical={compare("IdentifyingMarks")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <p>
                As required by KRS 605.090, the following history and risk
                factors regarding the child being placed were disclosed and
                discussed with the therapeutic foster parents.
              </p>
              <div className="container">
                <div className="row">
                  <div className="col">
                    <b>Abuse</b>
                  </div>
                  <div className="col">
                    <b>Behaviors</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Neglected"
                          label="Neglected"
                          checked={data.Neglected["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Neglected["@value"] === "true" && (
                          <Input
                            name="NeglectedDetails"
                            label="Please explain:"
                            error={errors.NeglectedDetails}
                            value={data.NeglectedDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("NeglectedDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="AttachmentDifficultiesBehavior"
                          label="Attachment Difficulties"
                          checked={
                            data.AttachmentDifficultiesBehavior["@value"] ===
                            "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.AttachmentDifficultiesBehavior["@value"] ===
                          "true" && (
                          <Input
                            name="AttachmentDifficultiesBehaviorDetails"
                            label="Please explain:"
                            error={errors.AttachmentDifficultiesBehaviorDetails}
                            defaultValue={
                              data.AttachmentDifficultiesBehaviorDetails[
                                "@value"
                              ]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "AttachmentDifficultiesBehaviorDetails"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="PhysicallyAbused"
                          label="Physically Abused"
                          checked={data.PhysicallyAbused["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.PhysicallyAbused["@value"] === "true" && (
                          <Input
                            name="PhysicallyAbusedDetails"
                            label="Please explain:"
                            error={errors.PhysicallyAbusedDetails}
                            defaultValue={
                              data.PhysicallyAbusedDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("PhysicallyAbusedDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="DestroysProperty"
                          label="Destroys Property"
                          checked={data.DestroysProperty["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.DestroysProperty["@value"] === "true" && (
                          <Input
                            name="DestroysPropertyDetails"
                            label="Please explain:"
                            error={errors.DestroysPropertyDetails}
                            defaultValue={
                              data.DestroysPropertyDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("DestroysPropertyDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="SexuallyAbused"
                          label="Sexually Abused or Exploited"
                          checked={data.SexuallyAbused["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.SexuallyAbused["@value"] === "true" && (
                          <Input
                            name="SexuallyAbusedDetails"
                            label="Please explain:"
                            error={errors.SexuallyAbusedDetails}
                            value={data.SexuallyAbusedDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("SexuallyAbusedDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="InnappropriateBehavior"
                          label="Inappropriate Sexual Acts or Behaviors"
                          checked={
                            data.InnappropriateBehavior["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.InnappropriateBehavior["@value"] === "true" && (
                          <Input
                            name="InnappropriateBehaviorDetails"
                            label="Please explain:"
                            error={errors.InnappropriateBehaviorDetails}
                            defaultValue={
                              data.InnappropriateBehaviorDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "InnappropriateBehaviorDetailsx"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="JuvenileSexOffender"
                          label=" Juvenile Sex Offender as defined by KRS 635.505(2)"
                          checked={
                            data.JuvenileSexOffender["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.JuvenileSexOffender["@value"] === "true" && (
                          <Input
                            name="JuvenileSexOffenderDetails"
                            label="Please explain:"
                            error={errors.JuvenileSexOffenderDetails}
                            defaultValue={
                              data.JuvenileSexOffenderDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("JuvenileSexOffenderDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="FireSetting"
                          label="Fire Setting"
                          checked={data.FireSetting["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.FireSetting["@value"] === "true" && (
                          <Input
                            name="FireSettingDetails"
                            label="Please explain:"
                            error={errors.FireSettingDetails}
                            value={data.FireSettingDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("FireSettingDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <b>Cooperation</b>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Hyperactivity"
                          label="Hyperactivity"
                          checked={data.Hyperactivity["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Hyperactivity["@value"] === "true" && (
                          <Input
                            name="HyperactivityDetails"
                            label="Please explain:"
                            error={errors.HyperactivityDetails}
                            value={data.HyperactivityDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("HyperactivityDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Cooperative"
                          label="Cooperative"
                          checked={data.Cooperative["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Cooperative["@value"] === "true" && (
                          <Input
                            name="CooperativeDetails"
                            label="Please explain:"
                            error={errors.CooperativeDetails}
                            value={data.CooperativeDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("CooperativeDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="SelfInjury"
                          label="Injury to Self (cutting, etc.)"
                          checked={data.SelfInjury["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.SelfInjury["@value"] === "true" && (
                          <Input
                            name="SelfInjuryDetails"
                            label="Please explain:"
                            error={errors.SelfInjuryDetails}
                            value={data.SelfInjuryDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("SelfInjuryDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="NonCooperative"
                          label="Non-Cooperative"
                          checked={data.NonCooperative["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.NonCooperative["@value"] === "true" && (
                          <Input
                            name="NonCooperativeDetails"
                            label="Please explain:"
                            error={errors.NonCooperativeDetails}
                            value={data.NonCooperativeDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("NonCooperativeDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Lying"
                          label="Lying"
                          checked={data.Lying["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Lying["@value"] === "true" && (
                          <Input
                            name="LyingDetails"
                            label="Please explain:"
                            error={errors.LyingDetails}
                            value={data.LyingDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("LyingDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <b>Health</b>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Friendly"
                          label="Makes Friends Easily"
                          checked={data.Friendly["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Friendly["@value"] === "true" && (
                          <Input
                            name="FriendlyDetails"
                            label="Please explain:"
                            error={errors.FriendlyDetails}
                            value={data.FriendlyDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("FriendlyDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="AttachmentDifficultiesHealth"
                          label="Attachment Difficulties"
                          checked={
                            data.AttachmentDifficultiesHealth["@value"] ===
                            "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.AttachmentDifficultiesHealth["@value"] ===
                          "true" && (
                          <Input
                            name="AttachmentDifficultiesHealthDetails"
                            label="Please explain:"
                            error={errors.AttachmentDifficultiesHealthDetails}
                            defaultValue={
                              data.AttachmentDifficultiesHealthDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare(
                              "AttachmentDifficultiesHealthDetails"
                            )}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="PhysicallyAggressive"
                          label="Physically Aggressive"
                          checked={
                            data.PhysicallyAggressive["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.PhysicallyAggressive["@value"] === "true" && (
                          <Input
                            name="PhysicallyAggressiveDetails"
                            label="Please explain:"
                            error={errors.PhysicallyAggressiveDetails}
                            defaultValue={
                              data.PhysicallyAggressiveDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("PhysicallyAggressiveDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Allergies"
                          label="Allergies"
                          checked={data.Allergies["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Allergies["@value"] === "true" && (
                          <Input
                            name="AllergiesDetails"
                            label="Please explain:"
                            error={errors.AllergiesDetails}
                            value={data.AllergiesDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("AllergiesDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Rocking"
                          label="Rocking, Head Banging, etc."
                          checked={data.Rocking["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Rocking["@value"] === "true" && (
                          <Input
                            name="RockingDetails"
                            label="Please explain:"
                            error={errors.RockingDetails}
                            value={data.RockingDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("RockingDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="DevelopmentalDelays"
                          label="Developmental Delays"
                          checked={
                            data.DevelopmentalDelays["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.DevelopmentalDelays["@value"] === "true" && (
                          <Input
                            name="DevelopmentalDelaysDetails"
                            label="Please explain:"
                            error={errors.DevelopmentalDelaysDetails}
                            defaultValue={
                              data.DevelopmentalDelaysDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("DevelopmentalDelaysDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="RunAway"
                          label="Running Away, (AWOL)"
                          checked={data.RunAway["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.RunAway["@value"] === "true" && (
                          <Input
                            name="RunAwayDetails"
                            label="Please explain:"
                            error={errors.RunAwayDetails}
                            value={data.RunAwayDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("RunAwayDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="EatingDisorders"
                          label="Eating Habits or Disorders"
                          checked={data.EatingDisorders["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.EatingDisorders["@value"] === "true" && (
                          <Input
                            name="EatingDisordersDetails"
                            label="Please explain:"
                            error={errors.EatingDisordersDetails}
                            value={data.EatingDisordersDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("EatingDisordersDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Humor"
                          label="Sense of Humor"
                          checked={data.Humor["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Humor["@value"] === "true" && (
                          <Input
                            name="HumorDetails"
                            label="Please explain:"
                            error={errors.HumorDetails}
                            value={data.HumorDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("HumorDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="FailureToThrive"
                          label="Failure to Thrive"
                          checked={data.FailureToThrive["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.FailureToThrive["@value"] === "true" && (
                          <Input
                            name="FailureToThriveDetails"
                            label="Please explain:"
                            error={errors.FailureToThriveDetails}
                            value={data.FailureToThriveDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("FailureToThriveDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="SexuallyAggressive"
                          label="Sexually Aggressive"
                          checked={data.SexuallyAggressive["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.SexuallyAggressive["@value"] === "true" && (
                          <Input
                            name="SexuallyAggressiveDetails"
                            label="Please explain:"
                            error={errors.SexuallyAggressiveDetails}
                            defaultValue={
                              data.SexuallyAggressiveDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("SexuallyAggressiveDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="MedicallyFragile"
                          label="Medically Fragile (including HIV)"
                          checked={data.MedicallyFragile["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.MedicallyFragile["@value"] === "true" && (
                          <Input
                            name="MedicallyFragileDetails"
                            label="Please explain:"
                            error={errors.MedicallyFragileDetails}
                            defaultValue={
                              data.MedicallyFragileDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("MedicallyFragileDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Smokes"
                          label="Smokes"
                          checked={data.Smokes["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Smokes["@value"] === "true" && (
                          <Input
                            name="SmokesDetails"
                            label="Please explain:"
                            error={errors.SmokesDetails}
                            value={data.SmokesDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("SmokesDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Medications"
                          label="Medications"
                          checked={data.Medications["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Medications["@value"] === "true" && (
                          <Input
                            name="MedicationsDetails"
                            label="Please explain:"
                            error={errors.MedicationsDetails}
                            value={data.MedicationsDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("MedicationsDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="SubstanceAbuse"
                          label="Substance Abuse Problems"
                          checked={data.SubstanceAbuse["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.SubstanceAbuse["@value"] === "true" && (
                          <Input
                            name="SubstanceAbuseDetails"
                            label="Please explain:"
                            error={errors.SubstanceAbuseDetails}
                            value={data.SubstanceAbuseDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("SubstanceAbuseDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="PhysicalHandicaps"
                          label="Physical Handicaps"
                          checked={data.PhysicalHandicaps["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.PhysicalHandicaps["@value"] === "true" && (
                          <Input
                            name="PhysicalHandicapsDetails"
                            label="Please explain:"
                            error={errors.PhysicalHandicapsDetails}
                            defaultValue={
                              data.PhysicalHandicapsDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("PhysicalHandicapsDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Stealing"
                          label="Stealing"
                          checked={data.Stealing["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Stealing["@value"] === "true" && (
                          <Input
                            name="StealingDetails"
                            label="Please explain:"
                            error={errors.StealingDetails}
                            value={data.StealingDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("StealingDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="NutritionalNeeds"
                          label="Special Nutritional Needs"
                          checked={data.NutritionalNeeds["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.NutritionalNeeds["@value"] === "true" && (
                          <Input
                            name="NutritionalNeedsDetails"
                            label="Please explain:"
                            error={errors.NutritionalNeedsDetails}
                            defaultValue={
                              data.NutritionalNeedsDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("NutritionalNeedsDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Suicidal"
                          label="Suicidal"
                          checked={data.Suicidal["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Suicidal["@value"] === "true" && (
                          <Input
                            name="SuicidalDetails"
                            label="Please explain:"
                            error={errors.SuicidalDetails}
                            value={data.SuicidalDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("SuicidalDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="SpeechDisorders"
                          label="Speech Disorders"
                          checked={data.SpeechDisorders["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.SpeechDisorders["@value"] === "true" && (
                          <Input
                            name="SpeechDisordersDetails"
                            label="Please explain:"
                            error={errors.SpeechDisordersDetails}
                            value={data.SpeechDisordersDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("SpeechDisordersDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="VerballyAggressive"
                          label="Verbally Aggressive"
                          checked={data.VerballyAggressive["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.VerballyAggressive["@value"] === "true" && (
                          <Input
                            name="VerballyAggressiveDetails"
                            label="Please explain:"
                            error={errors.VerballyAggressiveDetails}
                            defaultValue={
                              data.VerballyAggressiveDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("VerballyAggressiveDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="SleepingDifficulties"
                          label="Sleeping Difficulties"
                          checked={
                            data.SleepingDifficulties["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.SleepingDifficulties["@value"] === "true" && (
                          <Input
                            name="SleepingDifficultiesDetails"
                            label="Please explain:"
                            error={errors.SleepingDifficultiesDetails}
                            defaultValue={
                              data.SleepingDifficultiesDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("SleepingDifficultiesDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Wetting"
                          label="Wetting, Soiling, Smearing"
                          checked={data.Wetting["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Wetting["@value"] === "true" && (
                          <Input
                            name="WettingDetails"
                            label="Please explain:"
                            error={errors.WettingDetails}
                            value={data.WettingDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("WettingDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col">
                    <b>Personal</b>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="Talents"
                          label="Talents (sports, music, art, etc.)"
                          checked={data.Talents["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.Talents["@value"] === "true" && (
                          <Input
                            name="TalentsDetails"
                            label="Please explain:"
                            error={errors.TalentsDetails}
                            value={data.TalentsDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("TalentsDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="LikesDislikes"
                          label="Likes/Dislikes (food, animals, etc)"
                          checked={data.LikesDislikes["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.LikesDislikes["@value"] === "true" && (
                          <Input
                            name="LikesDislikesDetails"
                            label="Please explain:"
                            error={errors.LikesDislikesDetails}
                            value={data.LikesDislikesDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("LikesDislikesDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="ReligiousActivities"
                          label="Religious Activities"
                          checked={
                            data.ReligiousActivities["@value"] === "true"
                          }
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.ReligiousActivities["@value"] === "true" && (
                          <Input
                            name="ReligiousActivitiesDetails"
                            label="Please explain:"
                            error={errors.ReligiousActivitiesDetails}
                            defaultValue={
                              data.ReligiousActivitiesDetails["@value"]
                            }
                            onChange={handleChange}
                            identical={compare("ReligiousActivitiesDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="MusicalTastes"
                          label="Musical Tastes"
                          checked={data.MusicalTastes["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.MusicalTastes["@value"] === "true" && (
                          <Input
                            name="MusicalTastesDetails"
                            label="Please explain:"
                            error={errors.MusicalTastesDetails}
                            value={data.MusicalTastesDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("MusicalTastesDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <div className="row">
                      <div className="col">
                        <Checkbox
                          name="FavoriteColor"
                          label="Favorite Color"
                          checked={data.FavoriteColor["@value"] === "true"}
                          onChange={handleChange}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                      <div className="col">
                        {data.FavoriteColor["@value"] === "true" && (
                          <Input
                            name="FavoriteColorDetails"
                            label="Please explain:"
                            error={errors.FavoriteColorDetails}
                            value={data.FavoriteColorDetails["@value"]}
                            onChange={handleChange}
                            identical={compare("FavoriteColorDetailsx")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <Select
                      name="PlacementCount"
                      label=""
                      options={placementOptions}
                      error={errors.PlacementCount}
                      value={data.PlacementCount["@value"]}
                      onChange={handleChange}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <b>
                <u>Transportation &amp; Visitation</u>
              </b>
              <Input
                name="VisitationPlan"
                label="Visitation plan (including with whom the client will be visiting):"
                error={errors.VisitationPlan}
                value={data.VisitationPlan["@value"]}
                onChange={handleChange}
                identical={compare("VisitationPlan")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              Visitation plan (including with whom the client will be visiting):
              <br />
              <b>
                List all current Medications and Medications Received
                (prescription &amp; over the counter)
              </b>
              <div className="container">
                <div className="row">
                  <div className="col">Name of Medication</div>
                  <div className="col">Dosage</div>
                  <div className="col">Frequency</div>
                  <div className="col">Quantity Received</div>
                  <div className="col"># of Refills</div>
                  <div className="col">Date of Last Refill</div>
                </div>
                <div className="row mt-2">
                  <div className="col">
                    <Input
                      name="Name"
                      value={medicationData.Name}
                      onChange={handleMedicationChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Dosage"
                      value={medicationData.Dosage}
                      onChange={handleMedicationChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Frequency"
                      value={medicationData.Frequency}
                      onChange={handleMedicationChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Quantity"
                      value={medicationData.Quantity}
                      onChange={handleMedicationChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="Refills"
                      value={medicationData.Refills}
                      onChange={handleMedicationChange}
                      disabled={formDisabled}
                    />
                  </div>
                  <div className="col">
                    <Input
                      name="LastRefillDate"
                      type="date"
                      value={medicationData.LastRefillDate}
                      onChange={handleMedicationChange}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
              </div>
              <button
                className="btn btn-primary mb-2"
                onClick={handleAddMedication}
                disabled={medicationData.Name === ""}
              >
                Add Medication
              </button>
              <TextArea
                name="MedicationDetails"
                error={errors.MedicationDetails}
                value={data.MedicationDetails["@value"]}
                identical={compare("MedicationDetailsx")}
                comments={comments && parsedComments}
                addComment={() => setShowComment(true)}
                editComment={toggleCommentModal}
                deleteComment={handleDeleteComment}
                acknowledgeComment={handleAcknowledgeComment}
                disabled
              />
              <br />
              <b>
                <u>AUTHORIZATIONS</u>
              </b>
              <RadioButton
                name="ContactWithFamily"
                label="As part of SAFY's standard of care we make attempts to involve primary families in their Child's treatment while they are in care. This may include monthly communication regarding family therapy, treatment planning or updating parent on youth’s progress. In order to do this, we need permission from you. May SAFY have contact with the primary family?"
                options={yesNoOptions}
                error={errors.ContactWithFamily}
                value={data.ContactWithFamily["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.ContactWithFamily["@value"] === "0" && (
                <TextArea
                  name="NoContactDetails"
                  label="Provide the reason why we are not allowed to have contact with the primary family:"
                  error={errors.NoContactDetails}
                  value={data.NoContactDetails["@value"]}
                  identical={compare("NoContactDetailsx")}
                  commentsHandler={handleComments}
                  disabled
                />
              )}
              {data.ContactWithFamily["@value"] === "1" && (
                <>
                  <Input
                    name="ContactRestrictions"
                    label="Are there restrictions?"
                    error={errors.ContactRestrictions}
                    value={data.ContactRestrictions["@value"]}
                    onChange={handleChange}
                    identical={compare("ContactRestrictions")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  Please provide us with the following:
                  <div className="row">
                    <div className="col">
                      <Input
                        name="FamilyName"
                        label="Name:"
                        error={errors.FamilyName}
                        value={data.FamilyName["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilyName")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <PhoneInput
                        name="FamilyPhone"
                        label="Phone:"
                        error={errors.FamilyPhone}
                        value={data.FamilyPhone["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilyPhone")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6">
                      <TextArea
                        name="FamilyAddress"
                        label="Address:"
                        error={errors.FamilyAddress}
                        value={data.FamilyAddress["@value"]}
                        onChange={handleChange}
                        identical={compare("FamilyAddress")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </>
              )}
              <RadioButton
                name="Unsupervised"
                label="May client be left unsupervised?"
                options={yesNoOptions}
                error={errors.Unsupervised}
                value={data.Unsupervised["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              {data.Unsupervised["@value"] === "1" && (
                <TextArea
                  name="UnsupervisedDetails"
                  label="Describe amount of time or situations where the client may be left unsupervised:"
                  error={errors.UnsupervisedDetails}
                  value={data.UnsupervisedDetails["@value"]}
                  onChange={handleChange}
                  identical={compare("UnsupervisedDetails")}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
              )}
              If applicable supply Client's GAL or CASA worker contact
              information:
              <div className="row">
                <div className="col">
                  <Input
                    name="GALCASAName"
                    label="Name:"
                    error={errors.GALCASAName}
                    value={data.GALCASAName["@value"]}
                    onChange={handleChange}
                    identical={compare("GALCASAName")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <PhoneInput
                    name="GALCASAPhone"
                    label="Phone:"
                    error={errors.GALCASAPhone}
                    value={data.GALCASAPhone["@value"]}
                    onChange={handleChange}
                    identical={compare("GALCASAPhone")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
              We understand the information contained in this document and agree
              to fulfill our responsibility to making this Child's placement in
              this home successful.
              <div className="row">
                <div className="col">
                  <Input
                    name="TFCHomeAddress"
                    label="TFC Home Address"
                    error={errors.TFCHomeAddress}
                    value={data.TFCHomeAddress["@value"]}
                    onChange={handleChange}
                    identical={compare("TFCHomeAddress")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
                <div className="col">
                  <PhoneInput
                    name="EmergencyContact"
                    label="Emergency Contact # of SSW"
                    error={errors.EmergencyContact}
                    value={data.EmergencyContact["@value"]}
                    onChange={handleChange}
                    identical={compare("EmergencyContact")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                </div>
              </div>
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default KYF216;
